/**
 * Cookie names for CloudFront signed cookies
 */
export const CLOUDFRONT_COOKIES = {
  KEY_PAIR_ID: 'CloudFront-Key-Pair-Id',
  POLICY: 'CloudFront-Policy',
  SIGNATURE: 'CloudFront-Signature',
} as const

/**
 * Service to manage CloudFront signed cookies
 */
const cookieService = {
  /**
   * Sets a cookie with the given name and value
   */
  set: (name: string, value: string, expiresDate?: Date) => {
    try {
      let cookieString = `${name}=${value}; path=/; SameSite=Lax`

      if (expiresDate) {
        cookieString += `; expires=${expiresDate.toUTCString()}`
      }

      // Only set Secure flag in production
      if (window.location.protocol === 'https:') {
        cookieString += '; Secure'
      }

      document.cookie = cookieString
      return true
    } catch (error) {
      console.error('Error setting cookie:', error)
      return false
    }
  },

  /**
   * Gets a cookie value by name
   */
  get: (name: string): string | null => {
    try {
      const nameEQ = name + '='
      const cookies = document.cookie.split(';')

      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i]
        while (cookie.charAt(0) === ' ') {
          cookie = cookie.substring(1, cookie.length)
        }
        if (cookie.indexOf(nameEQ) === 0) {
          return cookie.substring(nameEQ.length, cookie.length)
        }
      }
      return null
    } catch (error) {
      console.error('Error getting cookie:', error)
      return null
    }
  },

  /**
   * Removes a cookie by name
   */
  remove: (name: string) => {
    try {
      // Set expiration date to the past to delete the cookie
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax`

      // Also try with Secure flag for HTTPS
      if (window.location.protocol === 'https:') {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax; Secure`
      }

      return true
    } catch (error) {
      console.error('Error removing cookie:', error)
      return false
    }
  },

  /**
   * Removes all CloudFront signed cookies
   */
  removeCloudFrontCookies: () => {
    try {
      cookieService.remove(CLOUDFRONT_COOKIES.KEY_PAIR_ID)
      cookieService.remove(CLOUDFRONT_COOKIES.POLICY)
      cookieService.remove(CLOUDFRONT_COOKIES.SIGNATURE)
      return true
    } catch (error) {
      console.error('Error removing CloudFront cookies:', error)
      return false
    }
  },

  /**
   * Checks if CloudFront cookies are present
   */
  hasCloudFrontCookies: (): boolean => {
    const keyPairId = cookieService.get(CLOUDFRONT_COOKIES.KEY_PAIR_ID)
    const policy = cookieService.get(CLOUDFRONT_COOKIES.POLICY)
    const signature = cookieService.get(CLOUDFRONT_COOKIES.SIGNATURE)

    return !!(keyPairId && policy && signature)
  },
}

export default cookieService
